import React, { useEffect, useState } from 'react';
import Sidebar from './Sidebar';
import { getAlerts, deleteAlert } from '../api';
import { logoutUser } from '../services/auth';
import { formatTimestamp, getCameraDisplayName } from '../utils/dateUtils';
import useWebSocket from '../hooks/useWebSocket';

const BellIcon = ({ size = 24, className = "" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M18 8a6 6 0 0 0-6-6v-1a1 1 0 0 0-2 0v1a6 6 0 0 0-6 6v3c0 2.22 1.21 4.3 3.42 5.37L10 21h4c2.21-1.07 3.42-3.15 3.42-5.37V8z" />
    <path d="M9 21v-1a3 3 0 0 1 6 0v1" />
  </svg>
);

const XIcon = ({ size = 16, className = "" }) => (
  <svg className={className} xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="18" y1="6" x2="6" y2="18"></line>
    <line x1="6" y1="6" x2="18" y2="18"></line>
  </svg>
);

const AlertsPage = ({ user }) => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // WebSocket integration for real-time alerts
  const {
    connectionState,
    isConnected,
    alerts: realTimeAlerts
  } = useWebSocket(user?.id, {
    autoConnect: true,
    onNewAlert: (alertData) => {
      console.log('🚨 [ALERTS PAGE] New alert received:', alertData);
      // Alert appears immediately on website - no delay
      if (alertData.status === 'detected') {
        console.log('⚡ [ALERTS PAGE] Fresh alert detected - showing immediately');
      }
      // Add to alerts list immediately
      setAlerts(prev => [alertData, ...prev]);
    },
    onAlertUpdate: (alertData) => {
      console.log('📝 [ALERTS PAGE] Alert update received:', alertData);
      if (alertData.status === 'processed') {
        console.log('✅ [ALERTS PAGE] Alert fully processed - database saved and WhatsApp sent');
      }
      // Update alert in list
      setAlerts(prev => 
        prev.map(alert => 
          alert.id === alertData.id ? { ...alert, ...alertData } : alert
        )
      );
    }
  });

  // Combine API alerts with real-time alerts
  useEffect(() => {
    const combinedAlerts = [...realTimeAlerts, ...alerts];
    // Remove duplicates based on alert ID or timestamp
    const uniqueAlerts = combinedAlerts.filter((alert, index, self) => 
      index === self.findIndex(a => 
        a.id === alert.id || 
        (a.timestamp === alert.timestamp && a.camera_id === alert.camera_id)
      )
    );
    // Sort by timestamp (newest first)
    setAlerts(uniqueAlerts.sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp)));
  }, [realTimeAlerts, alerts]);

  useEffect(() => {
    const loadAlerts = async () => {
      try {
        setLoading(true);
        const { data } = await getAlerts();
        setAlerts(data || []);
        setError(null);
      } catch (err) {
        setError("Failed to load alerts. Please try again.");
        setAlerts([]);
      } finally {
        setLoading(false);
      }
    };
    loadAlerts();
  }, []);

  const handleSignOut = () => {
    logoutUser();
    window.location.reload();
  };

  const handleDeleteAlert = async (alertId) => {
    try {
      console.log(`🗑️ [ALERTS PAGE] Deleting alert ${alertId}`);
      await deleteAlert(alertId);
      
      // Remove alert from local state
      setAlerts(prev => prev.filter(alert => alert.id !== alertId));
      
      console.log('✅ [ALERTS PAGE] Alert deleted successfully');
    } catch (error) {
      console.error('❌ [ALERTS PAGE] Failed to delete alert:', error);
      setError('Failed to delete alert. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="flex min-h-screen bg-gray-900 text-gray-200 font-sans">
        <Sidebar user={user} onSignOut={handleSignOut} />
        <div className="flex-1 p-8 overflow-y-auto">
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-32 w-32 border-t-2 border-b-2 border-blue-500"></div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen bg-gray-900 text-gray-200 font-sans">
      <Sidebar user={user} onSignOut={handleSignOut} />
      <div className="flex-1 p-8 overflow-y-auto">
        <header className="flex justify-between items-center mb-8">
          <h2 className="text-3xl font-bold text-white flex items-center">
            <BellIcon className="mr-3 text-red-400" />
            All Alerts
          </h2>
          <div className="flex items-center space-x-4">
            {/* WebSocket Connection Status */}
            <div className="flex items-center space-x-2">
              <div className={`w-2 h-2 rounded-full ${
                isConnected ? 'bg-green-400' : 
                connectionState === 'connecting' ? 'bg-yellow-400' : 
                'bg-red-400'
              }`}></div>
              <span className="text-xs text-gray-400">
                {isConnected ? 'Live Updates' : 
                 connectionState === 'connecting' ? 'Connecting...' : 
                 'Offline'}
              </span>
            </div>
            <span className="text-sm text-gray-400">
              {alerts.length} alert{alerts.length !== 1 ? 's' : ''} total
            </span>
          </div>
        </header>

        {error && (
          <div className="bg-red-900 bg-opacity-30 border border-red-500 text-red-200 px-4 py-3 rounded-lg mb-6">
            {error}
          </div>
        )}

        <div className="space-y-4">
          {alerts.length === 0 ? (
            <div className="bg-gray-800 rounded-xl shadow-xl p-8 border border-gray-700 text-center">
              <BellIcon className="mx-auto text-gray-500 mb-4" size={64} />
              <h3 className="text-xl font-semibold text-gray-300 mb-2">No Alerts Yet</h3>
              <p className="text-gray-500">
                Alerts will appear here when your cameras detect suspicious activity based on your AI prompts.
              </p>
            </div>
          ) : (
            alerts.map(alert => (
              <div key={alert.id} className="bg-gray-800 rounded-xl shadow-xl p-6 border border-gray-700">
                <div className="flex items-start justify-between">
                  <div className="flex-1 pr-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <span className="text-sm font-medium text-gray-400">
                        {formatTimestamp(alert.timestamp)}
                      </span>
                      <span className="bg-red-600 text-white text-xs px-2 py-1 rounded-full">
                        ALERT
                      </span>
                      {alert.status && (
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          alert.status === 'detected' 
                            ? 'bg-yellow-600 text-yellow-100' 
                            : alert.status === 'processed'
                            ? 'bg-green-600 text-green-100'
                            : 'bg-gray-600 text-gray-100'
                        }`}>
                          {alert.status === 'detected' ? 'DETECTED' : 
                           alert.status === 'processed' ? 'PROCESSED' : 
                           'UNKNOWN'}
                        </span>
                      )}
                    </div>
                    <h4 className="text-lg font-semibold text-white mb-2">
                      Camera: {getCameraDisplayName(alert)}
                    </h4>
                    <p className="text-gray-300 leading-relaxed">
                      {alert.ai_description || alert.ai_response}
                    </p>
                  </div>
                  <button
                    onClick={() => handleDeleteAlert(alert.id)}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-red-400 hover:bg-red-900 hover:bg-opacity-30 rounded-lg transition-colors duration-200"
                    title="Delete alert"
                  >
                    <XIcon size={20} />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AlertsPage;